import {
  CHECK_PAYMENT_METHOD,
  PAYER_TYPE_PATIENT,
  PAYMENT_METHODS
} from "constants/payments";
import {
  CHARGER_SUBTYPE,
  EAR_MOLD_SUBTYPE,
  OTHER_SUBTYPE,
  REMOTE_SUBTYPE
} from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import TITLES from "constants/titles";
import type { LineItem, Sale } from "services/types/sales.type";
import { formatMoneyValue } from "utils/common";

import {
  PAID_BY_METHODS,
  PA_PAYMENTS_FIELD_TAGS,
  PA_SALE_DETAILS_FIELD_TAGS,
  PA_TOTALS_FIELD_TAGS,
  paInvoiceTag
} from "../merge-tags-options/purchase-agreement-tags.utils";
import {
  excludeReturnedOrExchanged,
  getLineItemDiscountTotal,
  getLineItemNetTotal,
  getSaleTotals
} from "./common/utils";
import type { MergeTagsReplaceHandler } from "./merge-tags-values";

/*
 * ENG-2502 values: the invoice's money section and its sale-level facts (options in
 * merge-tags-options/purchase-agreement-tags.utils.ts). A totals row of the invoice table reads the table's
 * own numbers (getSaleTotals). A value only Anvil prints follows suno-backend
 * billing/views/purchase_agreement.py (generate_pa_and_invoice_finance_data), with two deliberate
 * differences (tech plan D11, D12):
 * - discounts count per unit × quantity, as the table does since ENG-2432 (Anvil sums the per-unit amount);
 * - payment ticks read every patient payment on the sale (Anvil reads only payments tied to hearing-aid
 *   lines, and its own code says it should read them all), and a check number prints only for a check.
 */

const money = (value: number) => formatMoneyValue(value, value < 0);

const sumOf = (lines: LineItem[], value: (item: LineItem) => number) =>
  lines.reduce((total, item) => total + value(item), 0);

const total = (item: LineItem) => Number(item.total) || 0;
const listTotal = (item: LineItem) =>
  (Number(item.price) || 0) * (Number(item.quantity) || 1);
const tax = (item: LineItem) => Number(item.tax_amount) || 0;
const patientResponsibility = (item: LineItem) =>
  Number(item.patient_responsibility) || 0;
const insurerResponsibility = (item: LineItem) =>
  Number(item.insurer_responsibility) || 0;

// Anvil's "devices": hearing aids and four accessory kinds, and only lines with a stocked item.
const AGREEMENT_ACCESSORY_SUBTYPES = new Set<number>([
  EAR_MOLD_SUBTYPE,
  REMOTE_SUBTYPE,
  CHARGER_SUBTYPE,
  OTHER_SUBTYPE
]);

const isAgreementDevice = (item: LineItem) => {
  if (!item.inventory_product) return false;
  const master = item.product?.master_product;
  if (master?.type === HEARING_AID_PRODUCT_TYPE) return !master.subtype;
  return (
    master?.type === ACCESSORY_PRODUCT_TYPE &&
    AGREEMENT_ACCESSORY_SUBTYPES.has(Number(master.subtype))
  );
};

const isService = (item: LineItem) =>
  item.product?.master_product?.type === SERVICE_PRODUCT_TYPE;

const lines = (sale: Sale) => excludeReturnedOrExchanged(sale.items);
const devices = (sale: Sale) => lines(sale).filter(isAgreementDevice);
const services = (sale: Sale) => lines(sale).filter(isService);

const devicesTotal = (sale: Sale) => sumOf(devices(sale), total);
const devicesWithoutTax = (sale: Sale) =>
  devicesTotal(sale) - sumOf(devices(sale), tax);
const devicesAndServicesTotal = (sale: Sale) =>
  devicesTotal(sale) + sumOf(services(sale), total);

const patientPayments = (sale: Sale) =>
  (sale.payments ?? []).filter(
    (payment) => payment.payer_type === PAYER_TYPE_PATIENT
  );
const lastPatientPayment = (sale: Sale) => patientPayments(sale).at(-1);

const optionalMoney = (value: unknown) =>
  value === null || value === undefined || value === ""
    ? ""
    : money(Number(value) || 0);

type SaleValue = (sale: Sale) => string;

export const TOTALS_VALUES: Record<string, SaleValue> = {
  TotalDiscounts: (sale) => money(getSaleTotals(sale).totalDiscounts),
  TotalTax: (sale) => money(getSaleTotals(sale).totalTax),
  AmountDueFromInsurance: (sale) =>
    money(getSaleTotals(sale).amountDueFromInsurance),
  AmountDueFromPatient: (sale) =>
    money(getSaleTotals(sale).amountDueFromPatient),
  Subtotal: (sale) => money(sumOf(lines(sale), listTotal)),
  PatientResponsibilityTotal: (sale) =>
    money(sumOf(lines(sale), patientResponsibility)),
  InsuranceResponsibilityTotal: (sale) =>
    money(sumOf(lines(sale), insurerResponsibility)),
  "AgreementDevices.Total": (sale) => money(devicesTotal(sale)),
  "AgreementDevices.Subtotal": (sale) => money(sumOf(devices(sale), listTotal)),
  "AgreementDevices.Discount": (sale) =>
    money(sumOf(devices(sale), getLineItemDiscountTotal)),
  "AgreementDevices.Tax": (sale) => money(sumOf(devices(sale), tax)),
  "AgreementDevices.TotalWithoutTax": (sale) => money(devicesWithoutTax(sale)),
  "AgreementDevices.PatientResponsibility": (sale) =>
    money(sumOf(devices(sale), patientResponsibility)),
  "AgreementDevices.InsuranceResponsibility": (sale) =>
    money(sumOf(devices(sale), insurerResponsibility)),
  "AgreementDevices.TotalMinusInsuranceBenefit": (sale) => {
    const benefit = Number(sale.insurance_policy?.hearing_benefit);
    return benefit ? money(devicesTotal(sale) - benefit) : "";
  },
  "DevicesAndServices.Total": (sale) => money(devicesAndServicesTotal(sale)),
  "DevicesAndServices.TotalWithoutTax": (sale) =>
    money(devicesWithoutTax(sale) + sumOf(services(sale), getLineItemNetTotal)),
  ServicesTotalWithoutTax: (sale) =>
    money(sumOf(services(sale), getLineItemNetTotal)),
  BalanceDueOnAgreement: (sale) =>
    money(
      devicesAndServicesTotal(sale) - getSaleTotals(sale).totalPatientPayments
    ),
  PatientBalanceDueOnInvoice: (sale) => {
    const { totalInsurancePayments, totalPatientPayments } =
      getSaleTotals(sale);
    return money(
      sumOf(lines(sale), total) - totalInsurancePayments - totalPatientPayments
    );
  }
};

export const PAYMENTS_VALUES: Record<string, SaleValue> = {
  TotalPatientPayments: (sale) =>
    money(getSaleTotals(sale).totalPatientPayments),
  TotalSurcharge: (sale) => money(getSaleTotals(sale).totalPatientSurcharge),
  TotalCharged: (sale) => {
    const { totalPatientPayments, totalPatientSurcharge } = getSaleTotals(sale);
    return money(totalPatientPayments + totalPatientSurcharge);
  },
  TotalInsurancePayments: (sale) =>
    money(getSaleTotals(sale).totalInsurancePayments),
  TotalPayments: (sale) => {
    const { totalInsurancePayments, totalPatientPayments } =
      getSaleTotals(sale);
    return money(totalInsurancePayments + totalPatientPayments);
  },
  PaymentMethods: (sale) => getSaleTotals(sale).paymentMethods.join(", "),
  "LastPatientPayment.Method": (sale) =>
    PAYMENT_METHODS.getFromCode(lastPatientPayment(sale)?.method),
  "LastPatientPayment.CheckNumber": (sale) => {
    const payment = lastPatientPayment(sale);
    return payment?.method === CHECK_PAYMENT_METHOD
      ? (payment.check_number ?? "")
      : "";
  },
  "LastPatientPayment.BankName": (sale) => {
    const payment = lastPatientPayment(sale);
    return payment?.method === CHECK_PAYMENT_METHOD
      ? ((payment as { bank_name?: string }).bank_name ?? "")
      : "";
  },
  "LastPatientPayment.Description": (sale) =>
    lastPatientPayment(sale)?.description ?? "",
  ...Object.fromEntries(
    PAID_BY_METHODS.map(({ code, tag }) => [
      tag,
      (sale: Sale) =>
        patientPayments(sale).some((payment) => payment.method === code)
          ? "X"
          : ""
    ])
  )
};

export const SALE_DETAILS_VALUES: Record<string, SaleValue> = {
  Insurer: (sale) => sale.insurance_policy?.insurer?.name ?? "",
  // A number with the flag off (the query asks only for the id), the plan with it on.
  ManagedCarePlan: (sale) => {
    const plan = sale.managed_care_plan as unknown;
    return typeof plan === "object" && plan
      ? ((plan as { name?: string }).name ?? "")
      : "";
  },
  "InsurancePolicy.Copay": (sale) =>
    optionalMoney(sale.insurance_policy?.copay),
  "InsurancePolicy.HearingAidBenefit": (sale) =>
    optionalMoney(sale.insurance_policy?.hearing_benefit),
  "InsurancePolicy.Deductible": (sale) =>
    optionalMoney(sale.insurance_policy?.deductible),
  ProviderNPI: (sale) => sale.provider?.npi ?? "",
  ProviderFullNameWithTitleAndSuffix: (sale) => {
    const provider = sale.provider;
    if (!provider) return "";
    return [
      provider.title ? TITLES.getFromCode(provider.title) : "",
      provider.first_name,
      provider.last_name,
      provider.suffix
    ]
      .filter(Boolean)
      .join(" ");
  }
};

const handlers = (fields: string[], values: Record<string, SaleValue>) =>
  fields.map((field) => [
    paInvoiceTag(field),
    ({ data }: { data: { contextSale?: Sale } }) =>
      data.contextSale ? values[field](data.contextSale) : ""
  ]);

export const PURCHASE_AGREEMENT_TOTALS_VALUES: MergeTagsReplaceHandler =
  Object.fromEntries([
    ...handlers(PA_TOTALS_FIELD_TAGS, TOTALS_VALUES),
    ...handlers(PA_PAYMENTS_FIELD_TAGS, PAYMENTS_VALUES),
    ...handlers(PA_SALE_DETAILS_FIELD_TAGS, SALE_DETAILS_VALUES)
  ]);
